import { appConfig } from "Config";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { defaultShouldDehydrateQuery } from "@tanstack/react-query";
import type { PersistQueryClientOptions } from "@tanstack/react-query-persist-client";
import { queryClient } from "./queryClient";

// localStorage keys. The cache blob is shared by every same-origin window; the
// owner key scopes it to a single user (see reconcileCacheUser).
const CACHE_KEY = "mp-query-cache";
const CACHE_USER_KEY = "mp-query-cache-user";

// Persisted snapshots older than this are dropped on restore rather than shown.
const MAX_AGE = 1000 * 60 * 60 * 24;

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: CACHE_KEY,
});

export const persistOptions: Omit<PersistQueryClientOptions, "queryClient"> = {
  persister,
  maxAge: MAX_AGE,
  // A new release busts the previous snapshot, so a shape change in any persisted
  // query can never hydrate stale-shaped data into the new code. `release` comes
  // from the build env (VITE_RELEASE); "dev" only applies under test where it's unset.
  buster: appConfig().release,
  dehydrateOptions: {
    // Allowlist: only queries explicitly tagged `meta.persist` reach storage —
    // never the whole cache. Compose with the default (success-only) predicate.
    shouldDehydrateQuery: (query) =>
      defaultShouldDehydrateQuery(query) && query.meta?.persist === true,
    // Queries-only snapshot: never persist (paused) mutations.
    shouldDehydrateMutation: () => false,
  },
};

/**
 * Query-option fragment for the boot-critical queries we snapshot (profile,
 * company list, scheme list): tag them for the dehydrate allowlist above, and
 * revalidate a stale hydrated copy on mount (overrides the global
 * `refetchOnMount: false`). Spread into a query hook's `query` options.
 */
export const PERSISTED_QUERY_OPTIONS = {
  refetchOnMount: true,
  meta: { persist: true },
} as const;

/**
 * Wipe both the in-memory and the persisted cache. Called on logout so the next
 * cold boot starts clean and a different user can never hydrate the previous
 * user's lists.
 */
export function clearPersistedCache(): void {
  queryClient.clear();
  persister.removeClient();
  window.localStorage.removeItem(CACHE_USER_KEY);
}

/**
 * Scope the persisted cache to one user. Records `userId` as the current owner
 * and, when the persisted cache belonged to a *different* known user (e.g. a
 * shared browser where the previous user didn't log out cleanly), drops it.
 *
 * Returns true when it cleared, so the caller can re-prime the now-empty cache.
 * A first-ever login (no recorded owner) does not clear — there is nothing to
 * leak, the cache holds only what this user just primed.
 */
export function reconcileCacheUser(userId: string): boolean {
  const previous = window.localStorage.getItem(CACHE_USER_KEY);
  const changed = previous !== null && previous !== userId;
  // Clear before recording the new owner, so an interrupted clear can never leave
  // the owner key pointing at data that wasn't actually dropped.
  if (changed) {
    queryClient.clear();
    persister.removeClient();
  }
  window.localStorage.setItem(CACHE_USER_KEY, userId);
  return changed;
}
